import {
  ContainerCreatedPayload,
  ShowFilteredComponentsChangedPayload,
  TreeChangedPayload,
} from '../actions/navigatorActions';
import { layout } from '../models/layout';
import { setClassNames } from '../utils/utils';
import type { NavigatorLayoutState } from '../types';
import { expandNode } from '../models/tree';
import { logError } from '@ardoq/logging';

const containerCreated = (
  state: NavigatorLayoutState,
  { container }: ContainerCreatedPayload
): NavigatorLayoutState => {
  return {
    ...state,
    container,
  };
};

const containerHeightChanged = (
  state: NavigatorLayoutState
): NavigatorLayoutState => {
  if (!state.container) {
    return state;
  }

  const height = state.container.offsetHeight;
  // Hidden container (offsetHeight 0): preserve the layout rather than
  // collapsing it to an empty tree while a display:none module is kept mounted.
  if (height === 0) {
    return state;
  }

  const scrollTop = state.container.scrollTop;
  // Skip when the layout already matches the container (width-only resize).
  // Compare the layout's own height/scrollTop, not state.height, so a layout
  // left stale while hidden — e.g. a scroll position that virtualizes to zero
  // rows — is rebuilt when the panel is shown again.
  if (
    state.rootLayoutBox &&
    state.rootLayoutBox.height === height &&
    state.rootLayoutBox.scrollTop === scrollTop
  ) {
    return state;
  }

  const { dragTargetNode, linkSourceNode, existingRefs } = state;
  return {
    ...state,
    height,
    scrollTop,
    rootLayoutBox: layout(
      state.tree,
      height,
      scrollTop,
      state.layoutNodeSet,
      setClassNames({ dragTargetNode, linkSourceNode, existingRefs }),
      state.showFilteredComponents
    ),
  };
};

const resetScroll = (state: NavigatorLayoutState): NavigatorLayoutState =>
  changeScrollTop(state, 0);

const containerScrolled = (
  state: NavigatorLayoutState
): NavigatorLayoutState => {
  if (!state.container) {
    return state;
  }
  const scrollTop = state.container.scrollTop;
  return changeScrollTop(state, scrollTop);
};

const changeScrollTop = (state: NavigatorLayoutState, scrollTop: number) => {
  const {
    dragTargetNode,
    linkSourceNode,
    existingRefs,
    shouldShowRefDrawing,
    linkSourceIds,
    hasLinkTarget,
  } = state;
  const multiselect = linkSourceIds ? linkSourceIds.length > 1 : false;
  return {
    ...state,
    scrollTop,
    rootLayoutBox: layout(
      state.tree,
      state.height,
      scrollTop,
      state.layoutNodeSet,
      setClassNames({
        dragTargetNode,
        linkSourceNode,
        existingRefs,
        shouldShowRefDrawing,
        hasLinkTarget,
        multiselect,
      }),
      state.showFilteredComponents
    ),
  };
};

const showFilteredComponentsChanged = (
  state: NavigatorLayoutState,
  payload: ShowFilteredComponentsChangedPayload
): NavigatorLayoutState => {
  const {
    dragTargetNode,
    linkSourceNode,
    existingRefs,
    shouldShowRefDrawing,
    linkSourceIds,
    hasLinkTarget,
  } = state;
  const multiselect = linkSourceIds ? linkSourceIds.length > 1 : false;
  return {
    ...state,
    showFilteredComponents: payload.showFilteredComponents,
    rootLayoutBox: layout(
      state.tree,
      state.height,
      state.scrollTop,
      state.layoutNodeSet,
      setClassNames({
        dragTargetNode,
        linkSourceNode,
        existingRefs,
        shouldShowRefDrawing,
        hasLinkTarget,
        multiselect,
      }),
      payload.showFilteredComponents
    ),
  };
};

const treeChanged = (
  state: NavigatorLayoutState,
  { reloadNodeIds, removedIds }: TreeChangedPayload
): NavigatorLayoutState => {
  const { highlightChangeRequests } = state;
  const { dragTargetNode, linkSourceNode, existingRefs } = state;
  if (removedIds) {
    removedIds.forEach(id => state.tree.removeNode(id));
  }
  (reloadNodeIds ?? []).forEach(reloadNodeId => {
    state.tree.getNode(reloadNodeId)?.reload();
  });
  return {
    ...state,
    highlightChangeRequests,
    rootLayoutBox: layout(
      state.tree,
      state.height,
      state.scrollTop,
      state.layoutNodeSet,
      setClassNames({ dragTargetNode, linkSourceNode, existingRefs }),
      state.showFilteredComponents
    ),
  };
};

const clearTree = (state: NavigatorLayoutState): NavigatorLayoutState => ({
  ...state,
  rootLayoutBox: layout(state.tree, 0, 0),
});

const setIsViewpointMode = (
  state: NavigatorLayoutState,
  { isViewpointMode }: { isViewpointMode: boolean }
): NavigatorLayoutState => ({
  ...state,
  isViewpointMode,
});

const treeChangedNoPayload = (state: NavigatorLayoutState) =>
  treeChanged(state, {});

const expandNodeInTree = (
  state: NavigatorLayoutState,
  { nodeId }: { nodeId: string }
): NavigatorLayoutState => {
  const node = state.tree.getNode(nodeId);
  if (!node) {
    return state;
  }
  expandNode(node);
  return navigatorLayoutOperations.treeChanged(state, {});
};

const initialComponentsSelectedInTree = (
  state: NavigatorLayoutState,
  selectedComponentIds: string[]
): NavigatorLayoutState => {
  for (const nodeId of selectedComponentIds) {
    const node = state.tree.getNode(nodeId);
    if (!node) {
      logError(Error('Node not found in tree'), null, { nodeId });
      continue;
    }
    expandNode(node);
  }
  return navigatorLayoutOperations.treeChanged(state, {});
};

/**
 * Keeps one node measured for a consumer outside the navigator: an id in
 * `layoutNodeSet` gets a box in `rootLayoutBox.layoutMap` whether or not its
 * row is rendered. The layout re-runs with the class names it already had.
 */
const setPinnedNode = (
  state: NavigatorLayoutState,
  nodeId: string | null
): NavigatorLayoutState => {
  if (state.pinnedNodeId === nodeId) {
    return state;
  }
  const { dragTargetNode, linkSourceNode, existingRefs } = state;
  const layoutNodeSet = new Set(state.layoutNodeSet);
  if (state.pinnedNodeId) {
    layoutNodeSet.delete(state.pinnedNodeId);
  }
  if (nodeId && state.tree.hasNode(nodeId)) {
    layoutNodeSet.add(nodeId);
  }
  return {
    ...state,
    pinnedNodeId: nodeId,
    layoutNodeSet,
    rootLayoutBox: layout(
      state.tree,
      state.height,
      state.scrollTop,
      layoutNodeSet,
      setClassNames({ dragTargetNode, linkSourceNode, existingRefs }),
      state.showFilteredComponents
    ),
  };
};

export const navigatorLayoutOperations = {
  containerCreated,
  containerHeightChanged,
  containerScrolled,
  resetScroll,
  showFilteredComponentsChanged,
  treeChanged,
  clearTree,
  setIsViewpointMode,
  treeChangedNoPayload,
  expandNodeInTree,
  initialComponentsSelectedInTree,
  setPinnedNode,
};
